/**
 * The audio a caption is made from (Phase 10), pure: the microphone's own
 * rate brought down to the 16 kHz the transcription service wants, cut into
 * tenths of a second — the service's own advice for live audio, and a caption
 * waits no longer for a chunk to fill — and let through only around speech.
 */

export const TARGET_RATE = 16_000
/** A tenth of a second at 16 kHz. */
export const CHUNK_SAMPLES = 1_600

/**
 * From `fromRate` to 16 kHz by averaging each output sample's share of the
 * input — a box filter, enough to keep speech clear of aliasing at the
 * rates microphones run at — carrying the fractional position across calls
 * so the joins between buffers are seamless.
 */
export class Downsampler {
  private readonly step: number
  private pos = 0
  private acc = 0
  private count = 0

  constructor(fromRate: number) {
    this.step = fromRate / TARGET_RATE
  }

  push(input: Float32Array): Int16Array {
    if (this.step <= 1) return toInt16(input)
    const out: number[] = []
    for (let i = 0; i < input.length; i++) {
      this.acc += input[i]!
      this.count += 1
      this.pos += 1
      if (this.pos >= this.step) {
        out.push(this.acc / this.count)
        this.pos -= this.step
        this.acc = 0
        this.count = 0
      }
    }
    return toInt16(out)
  }
}

export function toInt16(samples: ArrayLike<number>): Int16Array {
  const out = new Int16Array(samples.length)
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!))
    out[i] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff)
  }
  return out
}

/** Samples in, tenth-of-a-second chunks out. */
export class Chunker {
  private buf = new Int16Array(CHUNK_SAMPLES)
  private n = 0

  push(samples: Int16Array): Int16Array[] {
    const out: Int16Array[] = []
    let i = 0
    while (i < samples.length) {
      const take = Math.min(CHUNK_SAMPLES - this.n, samples.length - i)
      this.buf.set(samples.subarray(i, i + take), this.n)
      this.n += take
      i += take
      if (this.n === CHUNK_SAMPLES) {
        out.push(this.buf)
        this.buf = new Int16Array(CHUNK_SAMPLES)
        this.n = 0
      }
    }
    return out
  }
}

/**
 * Speech only. While quiet, the last `preRoll` chunks are held back, so the
 * start of the first word — heard before the speaking detector is sure —
 * goes out with the rest; after speech, `tail` more chunks carry the last
 * syllable. Nothing else leaves the browser.
 */
export class SpeechGate {
  private held: Array<{ chunk: Int16Array; at: number }> = []
  private tailLeft = 0

  /** Half a second before, eight tenths after. */
  constructor(
    private readonly preRoll = 5,
    private readonly tail = 8,
  ) {}

  /** One chunk, begun at `at`, while `speaking` or not; what to send now. */
  push(chunk: Int16Array, at: number, speaking: boolean): Array<{ chunk: Int16Array; at: number }> {
    if (speaking) {
      this.tailLeft = this.tail
      const out = [...this.held, { chunk, at }]
      this.held = []
      return out
    }
    if (this.tailLeft > 0) {
      this.tailLeft -= 1
      return [{ chunk, at }]
    }
    this.held.push({ chunk, at })
    if (this.held.length > this.preRoll) this.held.shift()
    return []
  }
}
